/**
 * DV-STAGE-003: Input Validation and Size Limits
 * Enforces hard caps, provides friendly errors, and validates JSON syntax
 */

export interface InputSizeResult {
  valid: boolean;
  sizeKB: number;
  sizeBytes: number;
  limitKB: number;
  error?: string;
  errorLocation?: {
    line: number;
    column: number;
    offset: number;
  };
}

export interface JsonValidationResult {
  valid: boolean;
  parsed?: any;
  error?: string;
  errorLocation?: {
    line: number;
    column: number;
    offset: number;
  };
}

export interface InputValidationResult {
  valid: boolean;
  input?: any;
  sizeKB: number;
  limitKB: number;
  error?: string;
  errorLocation?: {
    line: number;
    column: number;
    offset: number;
  };
}

// Input size limits (configurable per context)
export const INPUT_SIZE_LIMITS = {
  custom_json: 200, // 200 KB for custom JSON input
  sample_input: 500, // 500 KB for sample inputs
  effective_input: 200, // 200 KB for composed effective input
  status_report: 8, // 8 KB for status reports
} as const;

/**
 * Check input size with friendly error messages
 */
export function validateInputSize(
  input: any,
  limitKB: number = INPUT_SIZE_LIMITS.custom_json
): InputSizeResult {
  try {
    const jsonString = JSON.stringify(input);
    const sizeBytes = new Blob([jsonString]).size; // More accurate than string length
    const sizeKB = sizeBytes / 1024;

    if (sizeKB > limitKB) {
      return {
        valid: false,
        sizeKB: Math.round(sizeKB * 100) / 100, // Round to 2 decimals
        sizeBytes,
        limitKB,
        error: `Input too large: ${Math.round(sizeKB * 100) / 100}KB exceeds ${limitKB}KB limit. Consider reducing the data size or breaking it into smaller chunks.`
      };
    }

    return {
      valid: true,
      sizeKB: Math.round(sizeKB * 100) / 100,
      sizeBytes,
      limitKB
    };
  } catch (error) {
    return {
      valid: false,
      sizeKB: 0,
      sizeBytes: 0,
      limitKB,
      error: 'Input cannot be serialized to JSON'
    };
  }
}

/**
 * Parse and locate JSON syntax errors with line/column information
 */
export function validateJsonSyntax(jsonString: string): JsonValidationResult {
  if (!jsonString.trim()) {
    return {
      valid: false,
      error: 'JSON input is empty'
    };
  }

  try {
    const parsed = JSON.parse(jsonString);
    return {
      valid: true,
      parsed
    };
  } catch (error) {
    const errorLocation = extractJsonErrorLocation(error, jsonString);

    return {
      valid: false,
      error: error instanceof Error ? error.message : 'Invalid JSON syntax',
      errorLocation
    };
  }
}

/**
 * Extract line and column information from JSON parse errors
 */
function extractJsonErrorLocation(
  error: any,
  jsonString: string
): { line: number; column: number; offset: number } | undefined {
  if (!error || typeof error.message !== 'string') {
    return undefined;
  }

  // Try to extract position from common JSON error messages
  const positionMatch = error.message.match(/position (\d+)/i) ||
                       error.message.match(/at position (\d+)/i) ||
                       error.message.match(/character (\d+)/i);

  if (!positionMatch) {
    return undefined;
  }

  const offset = parseInt(positionMatch[1], 10);
  if (isNaN(offset) || offset < 0) {
    return undefined;
  }

  // Calculate line and column from offset
  const lines = jsonString.slice(0, offset).split('\n');
  const line = lines.length;
  const column = lines[lines.length - 1].length + 1;

  return { line, column, offset };
}

/**
 * Comprehensive input validation combining size and syntax checks
 */
export function validateInput(
  jsonString: string,
  limitKB: number = INPUT_SIZE_LIMITS.custom_json
): InputValidationResult {
  // First validate JSON syntax
  const syntaxResult = validateJsonSyntax(jsonString);

  if (!syntaxResult.valid) {
    return {
      valid: false,
      sizeKB: 0,
      limitKB,
      error: syntaxResult.error,
      errorLocation: syntaxResult.errorLocation
    };
  }

  // Then validate size
  const sizeResult = validateInputSize(syntaxResult.parsed, limitKB);

  if (!sizeResult.valid) {
    return {
      valid: false,
      sizeKB: sizeResult.sizeKB,
      limitKB,
      error: sizeResult.error
    };
  }

  return {
    valid: true,
    input: syntaxResult.parsed,
    sizeKB: sizeResult.sizeKB,
    limitKB
  };
}

/**
 * Format size for display
 */
export function formatSize(sizeKB: number): string {
  if (sizeKB < 1) {
    return `${Math.round(sizeKB * 1024)}B`;
  }
  if (sizeKB < 1024) {
    return `${Math.round(sizeKB * 100) / 100}KB`;
  }
  return `${Math.round(sizeKB / 1024 * 100) / 100}MB`;
}

/**
 * Format JSON with error recovery
 */
export function formatJson(jsonString: string): { formatted: string; error?: string } {
  try {
    const parsed = JSON.parse(jsonString);
    return {
      formatted: JSON.stringify(parsed, null, 2)
    };
  } catch (error) {
    return {
      formatted: jsonString,
      error: error instanceof Error ? error.message : 'Failed to format JSON'
    };
  }
}

/**
 * Truncate large content for display with standardized continuation markers
 */
export function truncateForDisplay(
  content: string,
  maxSizeKB: number = 8
): {
  truncated: string;
  wasTruncated: boolean;
  originalSizeKB: number;
  showRawViewButton: boolean;
} {
  const sizeBytes = new Blob([content]).size;
  const sizeKB = sizeBytes / 1024;

  if (sizeKB <= maxSizeKB) {
    return {
      truncated: content,
      wasTruncated: false,
      originalSizeKB: Math.round(sizeKB * 100) / 100,
      showRawViewButton: false
    };
  }

  const maxBytes = maxSizeKB * 1024;
  const truncated = content.slice(0, maxBytes);
  const excessKB = sizeKB - maxSizeKB;

  // Standardized truncation marker format
  const truncationMarker = `\n\n... (+${Math.round(excessKB * 100) / 100} KB)`;

  return {
    truncated: truncated + truncationMarker,
    wasTruncated: true,
    originalSizeKB: Math.round(sizeKB * 100) / 100,
    showRawViewButton: true
  };
}

/**
 * Check if user has admin permissions for raw view
 */
export async function canViewRawContent(): Promise<boolean> {
  try {
    const response = await fetch('/api/demo/permissions', {
      method: 'GET',
      credentials: 'include'
    });

    if (!response.ok) {
      return false;
    }

    const permissions = await response.json();
    return permissions.canViewRawContent || permissions.isAdmin || false;
  } catch (error) {
    console.warn('Error checking raw view permissions:', error);
    return false; // Default to no access on error
  }
}

/**
 * Get raw content with admin confirmation
 */
export async function getRawContent(
  contentId: string,
  confirmationToken?: string
): Promise<{
  success: boolean;
  content?: string;
  requiresConfirmation?: boolean;
  confirmationMessage?: string;
}> {
  try {
    const hasPermission = await canViewRawContent();
    if (!hasPermission) {
      return {
        success: false,
        requiresConfirmation: false
      };
    }

    // If no confirmation token provided, require confirmation first
    if (!confirmationToken) {
      return {
        success: false,
        requiresConfirmation: true,
        confirmationMessage: 'This will display unredacted content that may contain sensitive information. Continue?'
      };
    }

    const response = await fetch(`/api/demo/content/${contentId}/raw`, {
      method: 'GET',
      credentials: 'include',
      headers: {
        'X-Raw-View-Confirmation': confirmationToken
      }
    });

    if (!response.ok) {
      return { success: false };
    }

    const rawContent = await response.text();
    return {
      success: true,
      content: rawContent
    };
  } catch (error) {
    console.error('Error fetching raw content:', error);
    return { success: false };
  }
}

/**
 * Generate confirmation token for raw view
 */
export function generateRawViewConfirmationToken(): string {
  const timestamp = Date.now();
  const random = Math.random().toString(36).substr(2, 8);
  return `raw_${timestamp}_${random}`;
}

// Type exports handled inline above